import React, { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Boxes, PackageCheck, Warehouse } from "lucide-react";

import PageGate from "../../../components/PageGate";
import { PERMS } from "../../../utils/perms";
import { describeApiError } from "../../../lib/apiError";
import StoreShell from "../components/StoreShell";
import { useStoreGetItemQuery } from "../api/storesApi";
import {
  fmtDate, fmtQty, movementColor, movementLabel, recipient, signedQty, staffName,
} from "../utils/stores";

const naira = (n: number) =>
  `₦${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

/** Who did it — the person on the movement, however the row records them. */
function actor(m: any) {
  return (
    staffName(m.issued_by_admin ?? m.issuedByAdmin) ||
    m.recorded_by?.name ||
    m.recordedBy?.name ||
    "—"
  );
}

/**
 * One item, everything about it.
 *
 * Reached by clicking the item on a store's shelf, which is where the question
 * is asked: how much is here, who has some of it, and what has happened to it.
 * The three answers are the three bands below — where it sits, who holds it,
 * and the stock card in full.
 */
function ItemDetailInner() {
  const { id } = useParams();
  const { data: item, isLoading, error } = useStoreGetItemQuery(id as string, { skip: !id });

  const balances = useMemo(
    () => ((item?.balances ?? []) as any[]).slice().sort((a, b) => Number(b.quantity) - Number(a.quantity)),
    [item],
  );

  const holdings = useMemo(
    () => ((item?.holdings ?? []) as any[]).filter((h) => Number(h.quantity) > 0),
    [item],
  );

  const movements = useMemo(() => (item?.movements ?? []) as any[], [item]);

  /**
   * Who it went to and who brought it back, as a list of events.
   *
   * The stock card below has all of this, but reading it means picking the
   * relevant lines out of transfers, receipts and adjustments. This is the
   * question people actually ask of an item — "who has had this, and when" —
   * so it is answered on its own.
   *
   * Only the ADMIN side of each pair is taken: an issue writes two rows, and
   * counting both would show every handover twice.
   */
  const handovers = useMemo(
    () =>
      movements.filter(
        (m) => m.holder_type === "admin" && ["issue", "return", "sale"].includes(String(m.type)),
      ),
    [movements],
  );

  const reorder = Number(item?.reorder_level ?? 0);
  const total = Number(item?.quantity ?? 0);
  const low = total <= reorder;

  return (
    <StoreShell>
      <Link to="/stores/inventory" className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-900">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to inventory
      </Link>

      {error && (
        <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-sm font-medium text-red-700">
          {describeApiError(error, "Could not load that item.")}
        </div>
      )}

      {isLoading && <div className="py-14 text-center text-sm text-gray-400 animate-pulse">Loading…</div>}

      {item && (
        <>
          {/* What it is */}
          <div className={`bg-white border rounded-xl p-5 ${low ? "border-amber-300" : ""}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-xl font-bold text-gray-900">{item.name}</h1>
                  <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-lg bg-gray-100 text-gray-500 font-mono">
                    {item.code}
                  </span>
                  {!item.active && (
                    <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-lg bg-gray-100 text-gray-500">
                      Inactive
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {item.category || "No category"} · priced at {item.unit_cost != null ? naira(item.unit_cost) : "—"} per {item.unit}
                </p>
                {item.description && <p className="text-sm text-gray-500 mt-1">{item.description}</p>}
              </div>

              <div className={`rounded-xl border px-5 py-3 text-right ${low ? "bg-amber-50 border-amber-200" : "bg-gray-50"}`}>
                <div className={`text-2xl font-bold ${low ? "text-amber-700" : "text-gray-900"}`}>
                  {fmtQty(total)} <span className="text-sm font-medium text-gray-400">{item.unit}</span>
                </div>
                <div className="text-[11px] uppercase tracking-wider text-gray-400">In the stores</div>
                {low && (
                  <div className="text-[11px] font-semibold text-amber-700 mt-1 inline-flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> at or below reorder ({fmtQty(reorder)})
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            {/* Where it sits */}
            <div className="bg-white border rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Warehouse className="w-4 h-4 text-gray-400" />
                <h2 className="font-bold text-gray-800">Where it is</h2>
              </div>

              {balances.length === 0 ? (
                <p className="text-xs text-gray-400 py-6 text-center">
                  No store is carrying this item.
                </p>
              ) : (
                <div className="divide-y divide-gray-50">
                  {balances.map((b: any) => {
                    // Low is judged per STORE: a store can be out of something
                    // the Institute holds plenty of elsewhere.
                    const here = Number(b.quantity);
                    const lowHere = here <= reorder;
                    return (
                      <div key={b.id} className="flex items-center justify-between gap-3 py-2.5">
                        <div className="text-sm text-gray-800">{b.store?.name ?? "—"}</div>
                        <div className={`text-sm font-medium ${lowHere ? "text-amber-600" : "text-gray-900"}`}>
                          {fmtQty(here)} <span className="text-xs text-gray-400">{item.unit}</span>
                          {lowHere && <AlertTriangle className="w-3 h-3 inline ml-1 -mt-0.5" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Who is holding it */}
            <div className="bg-white border rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <PackageCheck className="w-4 h-4 text-gray-400" />
                <h2 className="font-bold text-gray-800">Who is holding it</h2>
              </div>

              {holdings.length === 0 ? (
                <p className="text-xs text-gray-400 py-6 text-center">
                  Nobody is holding any of this item.
                </p>
              ) : (
                <div className="divide-y divide-gray-50">
                  {holdings.map((h: any) => (
                    <div key={h.id} className="flex items-center justify-between gap-3 py-2.5">
                      <div className="text-sm text-gray-800">{staffName(h.admin)}</div>
                      <div className="text-sm font-medium text-gray-900">
                        {fmtQty(h.quantity)} <span className="text-xs text-gray-400">{item.unit}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <p className="text-[11px] text-gray-400 mt-3">
                Stock out with staff has left the stores, so it is not counted in the
                figures above.
              </p>
            </div>
          </div>

          {/* Who has had it */}
          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b">
              <h2 className="font-bold text-gray-800">Issued and returned</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Every time this item left a store into somebody's hands, came back, or was sold
                by them.
              </p>
            </div>

            {handovers.length === 0 ? (
              <p className="py-10 text-center text-xs text-gray-400">
                This item has never been issued to anybody.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b bg-gray-50/60">
                      <th className="px-4 py-2 font-medium">Date</th>
                      <th className="px-4 py-2 font-medium">What happened</th>
                      <th className="px-4 py-2 font-medium">Person</th>
                      <th className="px-4 py-2 font-medium text-right">Qty</th>
                      <th className="px-4 py-2 font-medium text-right">They then held</th>
                      <th className="px-4 py-2 font-medium">Recorded by</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {handovers.map((m: any) => (
                      <tr key={m.id} className="hover:bg-gray-50/60">
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(m.movement_date)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 text-[11px] border rounded-full ${movementColor(m.type)}`}>
                            {m.type === "issue" ? "Issued to them"
                              : m.type === "return" ? "Returned to store"
                              : "Sold by them"}
                          </span>
                          {m.sold_to && <div className="text-xs text-gray-400 mt-0.5">to {m.sold_to}</div>}
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900">{recipient(m)}</td>
                        <td className="px-4 py-3 text-right text-gray-800">
                          {signedQty(m)} <span className="text-xs text-gray-400">{item.unit}</span>
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600">{fmtQty(m.balance_after)}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{actor(m)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Everything that has happened to it */}
          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b">
              <div className="flex items-center gap-2">
                <Boxes className="w-4 h-4 text-gray-400" />
                <h2 className="font-bold text-gray-800">Stock card</h2>
              </div>
              <p className="text-xs text-gray-400 mt-0.5">
                Every movement of this item — what happened, where, to whom, and who recorded it.
              </p>
            </div>

            {movements.length === 0 ? (
              <p className="py-12 text-center text-xs text-gray-400">
                Nothing has moved yet. Receiving stock into a store is the first line.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b bg-gray-50/60">
                      <th className="px-4 py-2 font-medium">Date</th>
                      <th className="px-4 py-2 font-medium">What</th>
                      <th className="px-4 py-2 font-medium">Where / who</th>
                      <th className="px-4 py-2 font-medium text-right">Qty</th>
                      <th className="px-4 py-2 font-medium text-right">Balance</th>
                      <th className="px-4 py-2 font-medium">By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {movements.map((m: any) => (
                      <tr key={m.id} className="hover:bg-gray-50/60">
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(m.movement_date)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 text-[11px] border rounded-full ${movementColor(m.type)}`}>
                            {movementLabel(m.type)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {m.holder_type === "admin" ? (
                            <>Held by {recipient(m)}</>
                          ) : (
                            <>
                              {m.store?.name ?? "—"}
                              {m.counterpart_store?.name && (
                                <span className="text-xs text-gray-400">
                                  {m.type === "transfer_out" ? " → " : " ← "}{m.counterpart_store.name}
                                </span>
                              )}
                            </>
                          )}
                          {m.sold_to && <div className="text-xs text-gray-400">Sold to {m.sold_to}</div>}
                          {m.supplier && <div className="text-xs text-gray-400">From {m.supplier}</div>}
                          {m.reference && <div className="text-xs text-gray-400">Ref {m.reference}</div>}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-800">
                          {signedQty(m)} <span className="text-xs text-gray-400">{item.unit}</span>
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600">{fmtQty(m.balance_after)}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">
                          {actor(m)}
                          {m.note && (
                            <div className="text-gray-400 truncate max-w-[180px]" title={m.note}>{m.note}</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </StoreShell>
  );
}

export default function StoreItemDetailPage() {
  return (
    <PageGate perm={PERMS.store.access}>
      <ItemDetailInner />
    </PageGate>
  );
}
